import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useEffect } from "react";

import { useUserContext } from "@/context/UserContext";
import { fetchAndStoreWhoAmI } from "@/api/auth/whoami";

import Loader from "@/components/loader";
import { toast } from "react-toastify";

import {
  correlationHeaders,
  syncCorrelationIdFromHeaders,
} from "@/utils/correlationId";

const LoginCallback = () => {
  const navigate = useNavigate();
  const { setToken } = useUserContext();

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    const codeVerifier = localStorage.getItem("pkce_verifier");

    if (code) {
      fetch(`${import.meta.env.VITE_BASE_URL}/auth/callback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...correlationHeaders(),
        },
        body: JSON.stringify({ code, codeVerifier }),
      })
        .then(async (res) => {
          syncCorrelationIdFromHeaders(res.headers);
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || "Login failed");
          }
          return res.json();
        })
        .then(async (data) => {
          if (!data?.id_token) {
            throw new Error("Missing id_token");
          }

          sessionStorage.removeItem("pkce_verifier");

          localStorage.setItem("token", data.id_token);
          setToken(data.id_token);

          if (data.refresh_token) {
            localStorage.setItem("refresh_token", data.refresh_token);
          }

          const decodedIdToken = jwtDecode(data.id_token);

          if (decodedIdToken && typeof decodedIdToken === "object") {
            const { email, user_id: userId } = decodedIdToken;
            if (typeof email === "string") {
              localStorage.setItem("email", email);
            }
            if (typeof userId === "string" || typeof userId === "number") {
              localStorage.setItem("userId", String(userId));
            }

            const profileClaim = decodedIdToken.profile;
            let role = "Farmer";

            if (Array.isArray(profileClaim)) {
              role = profileClaim[0] || role;
            } else if (typeof profileClaim === "string") {
              try {
                const parsedProfile = JSON.parse(profileClaim);
                if (Array.isArray(parsedProfile)) {
                  role = parsedProfile[0] || role;
                } else if (typeof parsedProfile === "string") {
                  role = parsedProfile;
                }
              } catch (profileErr) {
                console.warn("Failed to parse profile claim:", profileErr);
              }
            }

            if (typeof decodedIdToken.role === "string") {
              role = decodedIdToken.role;
            }

            localStorage.setItem("role", role);
          } else {
            localStorage.setItem("role", "Farmer");
          }

          try {
            await fetchAndStoreWhoAmI();
          } catch (whoamiErr) {
            console.warn("Failed to fetch /whoami:", whoamiErr);
          }

          navigate("/home");
        })
        .catch((err) => {
          console.error("Login error:", err);
          toast.error(err.message || "Login failed");
          navigate("/login");
        });
    } else {
      navigate("/login");
      toast.error("Something went wrong, please try again later.");
    }
  }, [navigate, setToken]);

  return <Loader />;
};

export default LoginCallback;
